<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { Button, Icon } from '@oat-sa-private/ui-elements';
    import { generateElementId } from '@oat-sa-private/ui-core';
    import { createEventDispatcher, afterUpdate } from 'svelte';

    const dispatch = createEventDispatcher();

    // The open prop applies only to the inline variant of the Dialog
    // If enclosed in a ModalDialog, the parent will manage the state
    export let open = true;

    export let mode = 'inline'; // or 'modal'
    export let icon = '';
    export let iconStatus = 'neutral';
    export let heading = '';
    export let headingCentered = false;
    export let message = '';
    export let buttons = [];
    export let buttonsAlignment = 'end';

    let windowWidth;
    let dialogElement;
    let actionsElement;

    $: messageLines = message ? (Array.isArray(message) ? message : [message]) : [];

    afterUpdate(() => {
        // Make all undefined button.autoClose default to true
        for (let btn of buttons) {
            if (btn.autoClose !== false) {
                btn.autoClose = true;
            }
        }
    });
    $: allButtonsFullwidth = buttons.length === 1 || buttons.some(btn => btn.fullwidth);
    $: forceButtonsFullwidth = actionsElement && windowWidth && calculateButtonsFullWidth();

    if (!['end', 'start', 'center'].includes(buttonsAlignment)) {
        buttonsAlignment = 'end';
    }

    // For ARIA ids
    const headingId = generateElementId('heading');
    const messageId = generateElementId('description');

    /**
     * @fires open when it opens
     */
    $: if (open) {
        dispatch('open');
        if (dialogElement && !buttons.length) {
            dialogElement.focus();
        }
    }

    /**
     * Fires events & closes this dialog (or its parent), based on clicked Button
     * @param {String} key - identifier of actioned Button
     * @fires action - with button identifier
     * @fires close when it closes
     */
    function handleBtnClick(key) {
        const { autoClose } = buttons.find(btn => btn.key === key);

        dispatch('action', { key });

        if (autoClose) {
            if (mode === 'inline') {
                open = false;
            }
            dispatch('close');
        }
    }

    /**
     * Calculates whether buttons should be full width based on the total width of the buttons and the container.
     * It updates the `forceButtonsFullwidth`
     * @returns {Boolean}
     */
    function calculateButtonsFullWidth() {
        if (!actionsElement) {
            return false;
        }
        const actionsWidth = actionsElement.offsetWidth;
        const buttonsToCount = Array.from(actionsElement.querySelectorAll('button'));
        const totalButtonsWidth = buttonsToCount.reduce((totalWidth, button) => {
            const buttonStyle = window.getComputedStyle(button);
            const marginLeftRight = parseInt(buttonStyle.marginLeft, 10) + parseInt(buttonStyle.marginRight, 10);
            return totalWidth + button.offsetWidth + marginLeftRight;
        }, 0);

        return actionsWidth <= totalButtonsWidth;
    }
</script>

<style>
    .dialog {
        padding: var(--space-3x);
    }
    .inline {
        margin: 0 auto var(--space-2x);
    }
    .ui-heading {
        margin: 0 0 var(--space-2x);
    }
    .centered {
        text-align: center;
    }
    .actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        margin-top: var(--space-1x);
    }

    .actions.aligned-end {
        justify-content: flex-end;
    }
    .actions.aligned-start {
        justify-content: flex-start;
    }
    .actions.aligned-center {
        justify-content: center;
    }

    .icon-row {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: var(--space-3x);
    }
    .icon-container {
        display: flex;
        align-items: center;
        justify-content: center;

        --icon-container-height: 4rem;
        border-radius: 50%;
        min-height: var(--space-4x);
        min-width: var(--space-4x);

        &.success {
            background-color: var(--color-success-invert);
        }
        &.warning {
            background-color: var(--color-partial-invert);
        }
        &:not(.success):not(.warning) :global(.icon) {
            margin: auto;
            /* Forcing size of icons */
            min-width: var(--space-4x);
            height: var(--space-4x);
        }
    }
    /* larger than mobile */
    @media screen and (--mq-minwidth-small) {
        .inline {
            min-width: 50rem;
            max-width: 75rem;
        }
        .actions {
            flex-direction: row;
        }
        .actions :global(button) {
            margin-top: var(--space-2x);
        }
        .actions :global(button + button) {
            margin-inline-start: var(--space-2x);
        }
    }
    /* mobile */
    @media screen and (--mq-maxwidth-small) {
        .inline {
            max-width: calc(100% - 4rem);
        }
        .actions {
            flex-direction: column;
        }
        .actions :global(button) {
            margin: 0 0 var(--space-1x);
            &:first-child {
                margin-top: var(--space-2x);
            }
            &:last-child {
                margin-bottom: 0;
            }
            /* override to simulate passing 'size=small' prop: */
            font-size: var(--fontsize-body-xs);
            height: 5rem;
            /* override ends */
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} />
{#if open}
    <div
        class="dialog inverted"
        class:inline={mode === 'inline'}
        role="dialog"
        tabindex="-1"
        aria-labelledby={heading ? headingId : null}
        aria-describedby={messageLines.length ? messageId : null}
        bind:this={dialogElement}>
        {#if icon}
            <div class="icon-row">
                <div
                    class:warning={iconStatus === 'warning'}
                    class:success={iconStatus === 'success'}
                    class="icon-container">
                    <Icon name={icon} ariaHidden />
                </div>
            </div>
        {/if}
        {#if heading}
            <h2 id={headingId} class="ui-heading" class:centered={headingCentered === true}>{heading}</h2>
        {/if}
        {#if messageLines.length}
            <div id={messageId}>
                {#each messageLines as messageLine}
                    <p>{messageLine || ''}</p>
                {/each}
            </div>
        {/if}
        <slot />
        <div bind:this={actionsElement} class={`actions aligned-${buttonsAlignment}`}>
            {#each buttons as btnProps, i}
                <Button
                    {...btnProps}
                    shape="pill"
                    fullwidth={allButtonsFullwidth || forceButtonsFullwidth}
                    on:click={() => handleBtnClick(btnProps.key)} />
            {/each}
        </div>
    </div>
{/if}
